import { useState } from "react";
import {
  createTradingNoteTemplate,
  updateTradingNoteTemplate,
  deleteTradingNoteTemplate,
  getTradingNoteTemplatesByUser,
  useQuery,
  useAction,
} from "wasp/client/operations";
import type { TradingNoteTemplate } from "wasp/entities";
import { Button } from "../client/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import { Input } from "../client/components/ui/input";
import { Label } from "../client/components/ui/label";
import { Switch } from "../client/components/ui/switch";
import { Textarea } from "../client/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../client/components/ui/select";
import { toast } from "../client/hooks/use-toast";
import { Plus, Edit, Trash2, FileText } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../client/components/ui/dropdown-menu";
import useTranslation from "../client/hooks/useTranslation";

interface TemplateManagerProps {
  onSelectTemplate: (template: TradingNoteTemplate) => void;
  selectedTemplateId?: string;
  /** Render a smaller "Gérer" control (icon-only label hidden). Default false. */
  compact?: boolean;
  /** Override the Select trigger width/classes. Default "w-[200px]". */
  triggerClassName?: string;
}

// Helper to check if template is empty (used for clearing selection)
const isEmptyTemplate = (template: TradingNoteTemplate): boolean => {
  return !template.id || template.id === "";
};

export default function TemplateManager({
  onSelectTemplate,
  selectedTemplateId,
  compact = false,
  triggerClassName,
}: TemplateManagerProps) {
  const t = useTranslation();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<TradingNoteTemplate | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    content: "",
    isDefault: false,
  });

  const { data: templates, isLoading, refetch } = useQuery(
    getTradingNoteTemplatesByUser,
  );
  const createTemplateFn = useAction(createTradingNoteTemplate);
  const updateTemplateFn = useAction(updateTradingNoteTemplate);
  const deleteTemplateFn = useAction(deleteTradingNoteTemplate);

  const handleCreate = async () => {
    try {
      await createTemplateFn(formData);
      toast({
        title: t.templates.templateCreated,
        description: "Le template a été créé avec succès.",
      });
      setIsCreateDialogOpen(false);
      setFormData({ name: "", content: "", isDefault: false });
      refetch();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message || t.templates.cannotCreate,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (template: TradingNoteTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      content: template.content,
      isDefault: template.isDefault,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingTemplate) return;

    try {
      await updateTemplateFn({
        id: editingTemplate.id,
        ...formData,
      });
      toast({
        title: t.templates.templateUpdated,
        description: "Le template a été modifié avec succès.",
      });
      setIsEditDialogOpen(false);
      setEditingTemplate(null);
      setFormData({ name: "", content: "", isDefault: false });
      refetch();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message || t.templates.cannotUpdate,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (template: TradingNoteTemplate) => {
    if (!confirm(t.templates.confirmDelete)) return;

    try {
      await deleteTemplateFn({ id: template.id });
      toast({
        title: t.templates.templateDeleted,
        description: "Le template a été supprimé avec succès.",
      });
      refetch();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message || t.templates.cannotDelete,
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">{t.templates.loading}</div>;
  }

  const templatesList = templates || [];
  const selectedTemplate = templatesList.find((t) => t.id === selectedTemplateId);

  return (
    <>
      <div className="flex items-center gap-2">
        <Select
          value={selectedTemplateId || "none"}
          onValueChange={(value) => {
            if (value === "none") {
              // If no template selected, clear the selection
              onSelectTemplate({
                id: "",
                name: "",
                content: "",
                isDefault: false,
                isSystemDefault: false,
                createdAt: new Date(),
                updatedAt: new Date(),
                userId: "",
              } as TradingNoteTemplate);
            } else {
              const template = templatesList.find((t) => t.id === value);
              if (template) {
                onSelectTemplate(template);
              }
            }
          }}
        >
          <SelectTrigger className={triggerClassName ?? "w-[200px]"}>
            <SelectValue placeholder={t.templates.selectTemplate} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{t.templates.noTemplate}</SelectItem>
            {templatesList.map((template) => (
              <SelectItem key={template.id} value={template.id}>
                {template.name}
                {template.isDefault && ` (${t.templates.default})`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" title={compact ? t.templates.manage : undefined}>
              <FileText className={compact ? "h-4 w-4" : "h-4 w-4 mr-2"} />
              {!compact && t.templates.manage}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t.templates.createTemplate}
            </DropdownMenuItem>
            {templatesList.map((template) => (
              <div key={template.id}>
                <DropdownMenuItem onClick={() => handleEdit(template)}>
                  <Edit className="mr-2 h-4 w-4" />
                  {t.templates.edit} "{template.name}"
                </DropdownMenuItem>
                {!template.isSystemDefault && (
                  <DropdownMenuItem
                    onClick={() => handleDelete(template)}
                    className="text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t.templates.delete} "{template.name}"
                  </DropdownMenuItem>
                )}
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t.templates.createTemplate}</DialogTitle>
            <DialogDescription>
              {t.templates.createDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="template-name">{t.templates.nameLabel} *</Label>
              <Input
                id="template-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder={t.templates.namePlaceholder}
                required
              />
            </div>
            <div>
              <Label htmlFor="template-content">{t.templates.contentLabel} *</Label>
              <Textarea
                id="template-content"
                value={formData.content}
                onChange={(e) =>
                  setFormData({ ...formData, content: e.target.value })
                }
                rows={15}
                className="font-mono text-sm"
                placeholder={t.templates.contentPlaceholder}
                required
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="is-default"
                checked={formData.isDefault}
                onCheckedChange={(v) => setFormData({ ...formData, isDefault: v })}
              />
              <Label htmlFor="is-default" className="cursor-pointer">
                {t.templates.setAsDefault}
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateDialogOpen(false);
                setFormData({ name: "", content: "", isDefault: false });
              }}
            >
              {t.common.cancel}
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!formData.name || !formData.content}
            >
              {t.templates.create}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t.templates.editTemplate}</DialogTitle>
            <DialogDescription>
              {t.templates.editDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-template-name">{t.templates.nameLabel} *</Label>
              <Input
                id="edit-template-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-template-content">{t.templates.contentLabel} *</Label>
              <Textarea
                id="edit-template-content"
                value={formData.content}
                onChange={(e) =>
                  setFormData({ ...formData, content: e.target.value })
                }
                rows={15}
                className="font-mono text-sm"
                required
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="edit-is-default"
                checked={formData.isDefault}
                onCheckedChange={(v) => setFormData({ ...formData, isDefault: v })}
              />
              <Label htmlFor="edit-is-default" className="cursor-pointer">
                {t.templates.setAsDefault}
              </Label>
            </div>
            {editingTemplate?.isSystemDefault && (
              <p className="text-sm text-muted-foreground">
                {t.templates.systemDefaultNote}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditDialogOpen(false);
                setEditingTemplate(null);
                setFormData({ name: "", content: "", isDefault: false });
              }}
            >
              {t.common.cancel}
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={!formData.name || !formData.content}
            >
              {t.common.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

